export const PATHWAYS_SCHEMA_VERSION = "pathways_v1";
// Code identifier for the enquiry privacy wording; privacy owner approval is pending.
export const PRIVACY_NOTICE_VERSION = "pathways-enquiry-2026-10-04-v1";

export const TEAM_COURSE_OPTIONS = [
  ["access_aesthetics", "Access to aesthetic therapies"],
  ["skin_level4", "Advanced skin qualification"],
  ["skin_level5", "Further skin qualification"],
  ["laser_level4", "Laser hair removal"],
  ["tattoo_laser_level5", "Laser tattoo removal"],
  ["mesotherapy_level6", "Mesotherapy qualification"],
  ["injectables_bt7_diploma", "Injectables qualification"],
  ["injectables_bt7_certificate", "Injectables certificate"],
  ["medical_ma7d1", "Medical injectable qualification"],
  ["medical_ma7d2", "Medical injectable qualification"],
  ["medical_ma7c1", "Bio-stimulatory injectable qualification"],
  ["microneedling_cpd", "Microneedling CPD"],
  ["facial_skincare_cpd", "Facial and skincare"],
  ["lymphatic_cpd", "Lymphatic drainage"],
] as const;
export const TEAM_LEARNER_BAND_OPTIONS = [
  ["1", "1"], ["2_3", "2–3"], ["4_6", "4–6"],
  ["7_10", "7–10"], ["11_plus", "11+"], ["not_sure", "Not sure"],
] as const;
export const TEAM_GOAL_OPTIONS = [
  ["add_treatment_area", "Add a treatment area"],
  ["develop_existing_skills", "Develop existing skills"],
  ["qualification_progression", "Support qualification progression"],
  ["onboard_new_staff", "Train new staff"],
  ["mixed_unsure", "Mixed goals / help us decide"],
] as const;
export const TEAM_STAFF_BACKGROUND_OPTIONS = [
  ["beginners", "Beginners"],
  ["beauty_therapists", "Qualified beauty therapists"],
  ["cpd_practitioners", "CPD practitioners"],
  ["registered_healthcare_professionals", "Registered healthcare professionals"],
  ["mixed_unsure", "Mixed or unsure"],
] as const;
export const TEAM_TIMEFRAME_OPTIONS = [
  ["soon", "As soon as practical"],
  ["within_three_months", "Within three months"],
  ["three_to_six_months", "Three to six months"],
  ["exploring", "Exploring options"],
  ["unknown", "Not sure"],
] as const;
export const TEAM_DELIVERY_OPTIONS = [
  ["published_college_dates", "Published College dates"],
  ["staggered_dates", "Staggered attendance"],
  ["coordinated_private", "Discuss coordinated or private delivery"],
  ["unsure", "Not sure yet"],
] as const;
export const TEAM_BUSINESS_TYPE_OPTIONS = [
  ["salon", "Salon"], ["clinic", "Clinic"],
  ["pmu_business", "PMU business"], ["other_unsure", "Other / not sure"],
] as const;
export const TEAM_CONTACT_ROLE_OPTIONS = [
  ["owner", "Owner"], ["manager", "Manager"],
  ["coordinator", "Training coordinator"], ["other", "Other"],
] as const;

export type PathwayFormPrefill = {
  learnerStatus: string;
  areaOfInterest: string;
  bookingFor: string;
  trainingRoute: string;
  startTimeframe: string;
  practicalAttendance: string;
};

/** Maps confirmed quiz enums onto the legacy form's choices without inferring credentials. */
export function pathwayFormPrefill(answers: Record<string, unknown>): PathwayFormPrefill {
  const read = (...keys: string[]) => {
    for (const key of keys) if (typeof answers[key] === "string") return answers[key] as string;
    return "";
  };
  const background = read("primaryBackground", "primary_background", "background");
  const priorCpd = read("priorCpd", "prior_cpd");
  const goal = read("treatmentGoal", "treatment_goal", "goal", "area_of_interest");
  const booking = read("bookingFor", "booking_for", "training_for");
  const intent = read("trainingIntent", "training_intent");
  const timeframe = read("startTimeframe", "start_timeframe");
  const practical = read("canAttendPractical", "can_attend_practical");

  const backgroundMap: Record<string, string> = {
    beginner: "complete_beginner",
    beauty_therapist: "beauty_therapist",
    aesthetics_practitioner: priorCpd === "yes" ? "cpd_trained" : "unsure",
    medical_professional: "medic_healthcare",
    other: "unsure",
    unsure: "unsure",
    unknown: "unsure",
  };
  const goalMap: Record<string, string> = {
    beauty_facials: "beauty_therapy",
    advanced_skin: "skin_treatments",
    laser_ipl: "laser_ipl",
    tattoo_removal: "laser_ipl",
    injectables: "injectables_level_7",
    pmu: "pmu",
    hair: "hair",
    teaching_assessing: "teaching_assessing",
    unsure: "unsure",
    unknown: "unsure",
  };
  const trainingRoute = intent === "starting_out" || intent === "regulated_qualification"
    ? "regulated"
    : intent === "specific_skill"
      ? "cpd"
      : intent === "prior_learning_review"
        ? "rpl"
        : "unsure";

  return {
    learnerStatus: backgroundMap[background] || "",
    areaOfInterest: goalMap[goal] || "",
    bookingFor: booking === "team" ? "team" : booking === "self_and_team" ? "both" : booking === "self" ? "myself" : "unsure",
    trainingRoute,
    startTimeframe: timeframe === "soon" ? "asap" : timeframe === "within_three_months" ? "one_to_three_months" : timeframe === "three_to_six_months" ? "later" : timeframe === "exploring" || timeframe === "unknown" ? "unsure" : timeframe,
    practicalAttendance: practical === "yes" || practical === "no" ? practical : practical === "unknown" || practical === "unsure" ? "unsure" : "",
  };
}

/** One key is reused for identical retries in the mounted form, and replaced when its body changes. */
export function createIdempotencyKey() {
  const cryptoApi = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoApi && "randomUUID" in cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function splitContactName(value: string) {
  const name = value.trim().replace(/\s+/g, " ");
  const firstSpace = name.indexOf(" ");
  return firstSpace < 0
    ? { first_name: name, last_name: "" }
    : { first_name: name.slice(0, firstSpace), last_name: name.slice(firstSpace + 1) };
}

export function validateContactName(value: string) {
  const { first_name, last_name } = splitContactName(value);
  if (!first_name) return "Enter your name";
  return (first_name.length <= 100 && last_name.length <= 100) ||
    "Use up to 100 characters for each part of your name";
}

export function validateContactPhone(value: string, required: boolean) {
  const phone = value.trim();
  if (!required && !phone) return true;
  if (!phone) return "A phone number is needed for your selected contact method";
  if (!/^[+0-9()\s.-]+$/.test(phone)) return "Enter a valid phone number";
  const digitCount = phone.replace(/\D/g, "").length;
  return (digitCount >= 7 && digitCount <= 15) || "Enter a valid phone number";
}

export function currentSourcePage(fallback: string) {
  if (typeof window === "undefined") return fallback;
  return window.location.pathname || fallback;
}

export function getRetryKey(
  previous: { fingerprint: string; key: string } | null,
  requestBody: Record<string, unknown>,
) {
  const fingerprint = JSON.stringify(requestBody);
  if (previous?.fingerprint === fingerprint) return { ...previous, fingerprint };
  return { fingerprint, key: createIdempotencyKey() };
}

export function safeSummaryValue(value?: string | null) {
  return value?.trim() || "Not provided";
}
